<script setup lang="ts">
import { CtIcon } from '@camelot/components'
import { storeToRefs } from 'pinia'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useDateTimeStore } from '~/stores/datetime'
import { useLocationStore } from '~/stores/location'

const locationStore = useLocationStore()
const { startDate, endDate } = storeToRefs(useDateTimeStore())

function getDateOnly(date?: Date): string {
  if (!date)
    return '?'
  return date.toLocaleDateString(
    useTranslations().language.value,
    { weekday: 'short', day: '2-digit', month: '2-digit' },
  )
}

function getTimeOnly(date?: Date): string {
  if (!date)
    return '?'
  return date.toLocaleTimeString(
    useTranslations().language.value,
    { hour12: false, hour: '2-digit', minute: '2-digit' },
  )
}

const startDateOnly = computed<string>(() => getDateOnly(startDate.value.date))
const endDateOnly = computed<string>(() => getDateOnly(endDate.value.date))

const startHoursOnly = computed<string>(() => getTimeOnly(startDate.value.date))
const endHoursOnly = computed<string>(() => getTimeOnly(endDate.value.date))
</script>

<template>
  <section class="p-16 py-24 flex flex-col border-b-8 border-b-cotton">
    <h3 class="text-xl text-black-100">
      {{ translate('sidebar_booking_details') }}
    </h3>

    <header class="flex justify-between items-center mt-8">
      <h3 class="text-lg text-black-100 flex items-center">
        <CtIcon name="calendar" color="--ct-color-black-100" size="40" class="flex-none mr-8" />
        {{ translate('sidebar_date_place') }}
      </h3>
    </header>

    <section class="grid grid-flow-row-dense grid-cols-8 items-start mt-8 text-black-100">
      <aside class="flex flex-col items-center justify-between pt-4 pb-28 h-full max-h-[130px]">
        <div class="w-16 h-[21px] border-2 rounded-full">
          <p class="w-[6px] h-[6px] mt-[3px] ml-[3px] border-2 bg-black-100 rounded-full  " />
        </div>
        <span class="h-full border-r" />
        <div class="w-16 h-[21px] border-2 rounded-full" />
      </aside>

      <div class="col-span-7">
        <section>
          <h4 class="font-bold text-black-100">
            {{ startDateOnly }} | {{ startHoursOnly }}
          </h4>
          <p class="mt-8 text-black-80 text-xs leading-normal">
            {{ locationStore.address!.info }}
          </p>
        </section>

        <section class="mt-16">
          <h4 class="font-bold text-black-100">
            {{ endDateOnly }} | {{ endHoursOnly }}
          </h4>
          <p class="mt-8 text-xs leading-normal text-black-80">
            {{ translate('sidebar_return_vehicle').replace("{city}", locationStore.city.name) }}
          </p>
        </section>
      </div>
    </section>
  </section>
</template>
